import { CheckIcon, XIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { GuestModel, VisitorStatus, visitorStatusBadgeColorMap } from '@/models'

import { AccessTypeCell } from '../access-type-cell'
import {
  formatDateTime,
  formatPermissions,
  formatPermissionTimeRange,
} from '../visitors-dashboard.utils'
import * as S from './visitors-guests.styled'

export const VISITOR_STATUS_OPTIONS: { label: string; value: VisitorStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
  { label: 'Banned', value: 'banned' },
  { label: 'Suspended', value: 'suspended' },
]

export const visitorsGuestsInitialFilters: VisitorsGuestsFilters = {
  clientId: [],
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: 'all',
}

export const visitorsGuestsFiltersSchema = z.object({
  clientId: z.array(z.coerce.number()).optional(),
  limit: z.coerce.number().optional().default(DEFAULT_LIMIT),
  search: z.string().optional().default(''),
  skip: z.coerce.number().optional().default(0),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: z.enum(['all', 'active', 'expired', 'banned', 'suspended']).optional().default('all'),
})

export type VisitorsGuestsFilters = z.infer<typeof visitorsGuestsFiltersSchema>

export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

export const getGuestColumns = ({
  t,
  timezone,
  customAccessName,
  onCheckIn,
  residentColumnSettings,
}: {
  t: TFunction<['visitors']>
  timezone: string
  customAccessName?: string
  onCheckIn: (guest: GuestModel) => void
  residentColumnSettings: PersonColumnSettings
}): TableColumn<GuestModel>[] => [
  {
    id: 'name',
    render: (item) => (
      <S.NameCell>
        <Typography as="span" size="text-small" color="black800" breakLines={1}>
          {item.name}
        </Typography>
        {(item.type || item.phone) && (
          <Typography as="span" size="text-small" color="black800">
            {[item.type, item.phone].filter(Boolean).join(' | ')}
          </Typography>
        )}
      </S.NameCell>
    ),
    sortKey: 'name',
    title: t('visitors:columns.guestName'),
    width: '20rem',
  },
  {
    id: 'permissions',
    render: (item) => {
      const timeRange = formatPermissionTimeRange(item, timezone)
      return (
        <S.PermissionsCell>
          <S.PermissionRow>
            <S.RowText>
              <Typography as="span" size="text-small" color="black800">
                {formatPermissions(item, timezone)}
              </Typography>
            </S.RowText>
          </S.PermissionRow>
          {timeRange && (
            <Typography as="span" size="text-small" color="black800">
              {timeRange}
            </Typography>
          )}
        </S.PermissionsCell>
      )
    },
    sortAscLabel: t('visitors:columns.permissionsSortAsc'),
    sortDescLabel: t('visitors:columns.permissionsSortDesc'),
    sortKey: 'permission',
    title: t('visitors:columns.permissions'),
    width: '18rem',
  },
  {
    id: 'accessType',
    render: (item) => (
      <AccessTypeCell
        customAccess={item.customAccess}
        customAccessName={customAccessName}
        keyAccess={item.keyAccess}
        keylessAccess={item.keylessAccess}
        pinCode={item.pinCode}
      />
    ),
    title: t('visitors:dashboard.columns.accessType'),
    width: '16rem',
  },
  {
    id: 'currentAccess',
    render: (item) => (
      <Badge
        aria-label={
          item.permissionToEnter
            ? t('visitors:dashboard.status.active')
            : t('visitors:dashboard.status.inactive')
        }
        color={item.permissionToEnter ? 'green' : 'red'}
        size="small"
        variant="light"
      >
        <Icon
          icon={item.permissionToEnter ? CheckIcon : XIcon}
          color={item.permissionToEnter ? 'green500' : 'red500'}
          size="1.4rem"
        />
      </Badge>
    ),
    title: t('visitors:dashboard.columns.currentAccess'),
    width: '15rem',
  },
  {
    id: 'lastCheckIn',
    render: (item) => {
      const { date, time } = formatDateTime(item.lastCheckIn, timezone)
      if (!item.lastCheckIn) return EMPTY_VALUE
      return (
        <S.DateTimeCell>
          <Typography as="span" size="text-small" color="black800">
            {date}
          </Typography>
          <Typography as="span" size="text-small" color="black800">
            {time}
          </Typography>
        </S.DateTimeCell>
      )
    },
    sortAscLabel: t('visitors:dashboard.columns.lastCheckInSortAsc'),
    sortDescLabel: t('visitors:dashboard.columns.lastCheckInSortDesc'),
    sortKey: 'lastCheckIn',
    title: t('visitors:dashboard.columns.lastCheckIn'),
    width: '14rem',
  },
  getPersonColumn(residentColumnSettings, {
    getDisplay: (row) => row.residentDisplay,
    getPerson: (item) =>
      item.residentFirstName || item.residentName
        ? {
            apartment: item.unit,
            avatar: item.residentAvatar,
            buildingNumber: item.residentBuildingNumber,
            clientId: item.clientId,
            email: item.residentEmail,
            firstName: item.residentFirstName ?? item.residentName,
            kind: 'resident',
            lastName: item.residentFirstName ? item.residentLastName : null,
            phone: item.residentPhone,
            street: item.residentStreet,
          }
        : null,
    id: 'residentUnit',
    pageName: t('visitors:columns.guestsPageName'),
    renderFallback: (item) => item.unit || EMPTY_VALUE,
    sortKey: 'resident',
    source: 'visitors',
    title: t('visitors:columns.resident'),
  }),
  {
    id: 'status',
    render: (item) => (
      <Badge color={visitorStatusBadgeColorMap[item.status]} size="small" variant="light">
        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
      </Badge>
    ),
    title: t('visitors:columns.status'),
    width: '10rem',
  },
  {
    id: 'actions',
    render: (item) => {
      if (item.status !== 'active') return null
      return (
        <Button
          use="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onCheckIn(item)
          }}
        >
          {t('visitors:columns.checkIn')} <Icon icon={CheckIcon} size="1.4rem" />
        </Button>
      )
    },
    title: t('visitors:columns.actions'),
    width: '14rem',
  },
]
